/**
 * Coupons hooks using React Relay
 *
 * Note: Run `npm run relay` after making changes to GraphQL queries
 * to regenerate the type artifacts.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useLazyLoadQuery, usePaginationFragment, fetchQuery } from 'react-relay';
import graphql from 'babel-plugin-relay/macro';
import { relayEnvironment } from '../../../../relay';
import type {
  useCouponsRelayQuery as UseCouponsRelayQuery,
  useCouponsRelayQuery$variables,
  CouponFilterInput,
  CouponSortInput,
} from './__generated__/useCouponsRelayQuery.graphql';
import type { useCouponsRelayDirectQuery as UseCouponsRelayDirectQuery } from './__generated__/useCouponsRelayDirectQuery.graphql';
import type { useCouponsRelayByIdQuery } from './__generated__/useCouponsRelayByIdQuery.graphql';
import type { useCouponsRelayPaginationFragment$key } from './__generated__/useCouponsRelayPaginationFragment.graphql';

// ============================================================================
// GraphQL Definitions
// ============================================================================

const CouponsListPaginationFragment = graphql`
  fragment useCouponsRelayPaginationFragment on RootQueryType
  @refetchable(queryName: "useCouponsRelayPaginationQuery")
  @argumentDefinitions(
    first: { type: "Int", defaultValue: 100 }
    after: { type: "String" }
    filter: { type: "CouponFilterInput" }
    sort: { type: "[CouponSortInput!]" }
    applicableToSystem: { type: "String" }
  ) {
    listCoupons(
      first: $first
      after: $after
      filter: $filter
      sort: $sort
      applicableToSystem: $applicableToSystem
    )
      @connection(
        key: "useCouponsRelayPaginationFragment_listCoupons"
        filters: ["filter", "sort", "applicableToSystem"]
      ) {
      count
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      edges {
        cursor
        node {
          id
          oldId
          name
          code
          active
          discount
          discountType
          duration
          uses
          systemId
          systemOldId
          organizationId
          assignmentOnly
          validDays
          validTimeStart
          validTimeEnd
          validFrom
          validUntil
          created
        }
      }
    }
  }
`;

const CouponsListQuery = graphql`
  query useCouponsRelayQuery(
    $first: Int
    $after: String
    $filter: CouponFilterInput
    $sort: [CouponSortInput!]
    $applicableToSystem: String
  ) {
    ...useCouponsRelayPaginationFragment
      @arguments(
        first: $first
        after: $after
        filter: $filter
        sort: $sort
        applicableToSystem: $applicableToSystem
      )
  }
`;

const CouponsListDirectQuery = graphql`
  query useCouponsRelayDirectQuery(
    $first: Int
    $after: String
    $filter: CouponFilterInput
    $sort: [CouponSortInput!]
    $applicableToSystem: String
  ) {
    listCoupons(
      first: $first
      after: $after
      filter: $filter
      sort: $sort
      applicableToSystem: $applicableToSystem
    ) {
      count
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      edges {
        cursor
        node {
          id
          oldId
          name
          code
          active
          discount
          discountType
          duration
          uses
          systemId
          systemOldId
          organizationId
          assignmentOnly
          validDays
          validTimeStart
          validTimeEnd
          validFrom
          validUntil
          created
        }
      }
    }
  }
`;

const CouponByIdQuery = graphql`
  query useCouponsRelayByIdQuery($id: ID!) {
    getCoupon(id: $id) {
      id
      oldId
      name
      code
      active
      discount
      discountType
      duration
      uses
      systemId
      systemOldId
      organizationId
      assignmentOnly
      validDays
      validTimeStart
      validTimeEnd
      validFrom
      validUntil
      created
    }
  }
`;

// ============================================================================
// Types
// ============================================================================

export type { CouponFilterInput, CouponSortInput };

export interface CouponGraphQL {
  id: string;
  oldId?: string | null;
  name: string;
  code?: string | null;
  active?: boolean | null;
  discount?: number | null;
  discountType?: string | null;
  duration?: number | null;
  uses?: number | null;
  systemId?: string | null;
  systemOldId: string;
  organizationId?: string | null;
  assignmentOnly?: boolean | null;
  validDays?: number | null;
  validTimeStart?: number | null;
  validTimeEnd?: number | null;
  validFrom?: string | null;
  validUntil?: string | null;
  created?: string | null;
}

export interface CouponQueryFilter {
  systemId?: string;
  systemOldId?: string;
  organizationId?: string;
  /**
   * Read-action argument: when set, returns coupons whose scope applies to
   * the given system (system-scoped coupons for that system AND org-scoped
   * coupons inherited from the system's organization).
   *
   * Passed as a top-level argument to `listCoupons`, NOT inside the filter
   * input object.
   */
  applicableToSystem?: string;
  active?: boolean;
  name?: string;
  code?: string;
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Suspense-based hook for fetching coupons list
 */
export function useCouponsRelay(
  filter?: CouponQueryFilter,
  options?: {
    limit?: number;
    sort?: CouponSortInput[];
  }
) {
  const limit = options?.limit ?? 100;
  const sort = options?.sort ?? [{ field: 'NAME', order: 'ASC' }];

  const variables: useCouponsRelayQuery$variables = {
    first: limit,
    filter: buildRelayFilter(filter),
    sort,
    applicableToSystem: filter?.applicableToSystem,
  };

  const queryData = useLazyLoadQuery<UseCouponsRelayQuery>(CouponsListQuery, variables);

  const { data, loadNext, hasNext, isLoadingNext, refetch } = usePaginationFragment<
    UseCouponsRelayQuery,
    useCouponsRelayPaginationFragment$key
  >(CouponsListPaginationFragment, queryData);

  const nodes = data.listCoupons?.edges?.map((edge) => edge?.node).filter(Boolean) ?? [];

  return {
    data: nodes as CouponGraphQL[],
    count: data.listCoupons?.count,
    pageInfo: data.listCoupons?.pageInfo,
    loadNext,
    hasNext,
    isLoadingNext,
    refetch,
  };
}

/**
 * Suspense-based hook for fetching a single coupon by ID
 */
export function useCouponByIdRelay(couponId: string) {
  const data = useLazyLoadQuery<useCouponsRelayByIdQuery>(CouponByIdQuery, {
    id: couponId,
  });

  return data.getCoupon as CouponGraphQL | null;
}

// ============================================================================
// React Query-like Interface (No Suspense)
// ============================================================================

/**
 * Hook that provides React Query-like interface for coupons data
 */
export function useCouponsRelayQuery(
  filter?: CouponQueryFilter,
  options?: {
    limit?: number;
    sort?: CouponSortInput[];
    enabled?: boolean;
  }
) {
  const [data, setData] = useState<CouponGraphQL[] | null>(null);
  const [count, setCount] = useState<number | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetched, setIsFetched] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [fetchCount, setFetchCount] = useState(0);

  const limit = options?.limit ?? 100;
  const sort = useMemo(
    () => options?.sort ?? [{ field: 'NAME' as const, order: 'ASC' as const }],
    [options?.sort]
  );
  const enabled = options?.enabled !== false;

  const filterKey = useMemo(() => JSON.stringify(filter), [filter]);

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const doFetch = async () => {
      try {
        if (isFetched) {
          setIsRefetching(true);
        } else {
          setIsLoading(true);
        }

        const variables: useCouponsRelayQuery$variables = {
          first: limit,
          filter: buildRelayFilter(filter),
          sort,
          applicableToSystem: filter?.applicableToSystem,
        };

        const result = await fetchQuery<UseCouponsRelayDirectQuery>(
          relayEnvironment,
          CouponsListDirectQuery,
          variables
        ).toPromise();

        if (cancelled) return;

        if (result) {
          const nodes =
            result.listCoupons?.edges?.map((edge) => edge?.node).filter(Boolean) ?? [];
          setData(nodes as CouponGraphQL[]);
          setCount(result.listCoupons?.count ?? undefined);
          setError(null);
        }
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err : new Error('Failed to fetch coupons'));
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setIsFetched(true);
          setIsRefetching(false);
        }
      }
    };

    doFetch();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, filterKey, limit, sort, fetchCount]);

  const refetch = useCallback(() => {
    setFetchCount((c) => c + 1);
  }, []);

  return {
    data,
    count,
    isLoading,
    isFetched,
    isRefetching,
    error,
    refetch,
  };
}

/**
 * Hook for fetching a single coupon by ID with explicit loading state
 */
export function useCouponByIdRelayQuery(
  couponId: string | undefined,
  options?: { enabled?: boolean }
) {
  const [data, setData] = useState<CouponGraphQL | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetched, setIsFetched] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [fetchCount, setFetchCount] = useState(0);

  const enabled = options?.enabled !== false && Boolean(couponId);

  useEffect(() => {
    if (!enabled || !couponId) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const doFetch = async () => {
      try {
        setIsLoading(true);

        const result = await fetchQuery<useCouponsRelayByIdQuery>(
          relayEnvironment,
          CouponByIdQuery,
          { id: couponId }
        ).toPromise();

        if (cancelled) return;

        if (result) {
          setData(result.getCoupon as CouponGraphQL | null);
          setError(null);
        }
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err : new Error('Failed to fetch coupon'));
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setIsFetched(true);
        }
      }
    };

    doFetch();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, couponId, fetchCount]);

  const refetch = useCallback(() => {
    setFetchCount((c) => c + 1);
  }, []);

  return { data, isLoading, isFetched, error, refetch };
}

// ============================================================================
// Helpers
// ============================================================================

function buildRelayFilter(filter?: CouponQueryFilter): CouponFilterInput | undefined {
  if (!filter) return undefined;

  const relayFilter: CouponFilterInput = {};

  if (filter.systemId) {
    relayFilter.systemId = { eq: filter.systemId };
  }
  if (filter.systemOldId) {
    relayFilter.systemOldId = { eq: filter.systemOldId };
  }
  if (filter.organizationId) {
    relayFilter.organizationId = { eq: filter.organizationId };
  }
  if (filter.active !== undefined) {
    relayFilter.active = { eq: filter.active };
  }
  if (filter.name) {
    if (filter.name.includes('%')) {
      relayFilter.name = { ilike: filter.name };
    } else {
      relayFilter.name = { ilike: `%${filter.name}%` };
    }
  }
  if (filter.code) {
    relayFilter.code = { eq: filter.code };
  }

  return Object.keys(relayFilter).length > 0 ? relayFilter : undefined;
}
